<template>
    <div class="field-diff" data-testid="field-diff">
        <table v-if="rows.length" class="field-diff-table">
            <thead>
                <tr>
                    <th class="field-diff-key">Field</th>
                    <th>{{ fromName }}</th>
                    <th>{{ toName }}</th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="row in rows"
                    :key="row.key"
                    class="field-diff-row is-changed"
                    :class="`is-${row.kind}`"
                    data-testid="field-diff-row"
                >
                    <td class="field-diff-key">{{ row.key }}</td>
                    <td class="field-diff-from">
                        <span class="field-diff-value">{{
                            row.fromDisplay
                        }}</span>
                    </td>
                    <td class="field-diff-to">
                        <span class="field-diff-value">{{
                            row.toDisplay
                        }}</span>
                    </td>
                </tr>
            </tbody>
        </table>
        <p v-else class="field-diff-empty" data-testid="field-diff-empty">
            No field changes.
        </p>
    </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";

type FieldObject = Record<string, unknown> | null;

const props = withDefaults(
    defineProps<{
        from: FieldObject;
        to: FieldObject;
        fromName?: string;
        toName?: string;
    }>(),
    {
        fromName: "From",
        toName: "To",
    }
);

type RowKind = "added" | "removed" | "changed";

interface FieldRow {
    key: string;
    fromDisplay: string;
    toDisplay: string;
    kind: RowKind;
}

// Shown for a key that is absent on one side (an added/removed field).
const ABSENT = "—";

const formatValue = (value: unknown): string => {
    if (value === null) return "null";
    if (typeof value === "string") return value;
    // Nested objects/arrays (e.g. an entity's attributes map) — compact JSON.
    return JSON.stringify(value);
};

// Both sides come from JSON.parse of the backend's sort_keys=True dump, so a
// stable stringify is a reliable deep-equality check for primitives and nesting.
const stableEqual = (a: unknown, b: unknown): boolean =>
    JSON.stringify(a) === JSON.stringify(b);

// One row per key that differs between `from` and `to`; unchanged keys omitted.
// A null side is treated as the empty object, so every key on the other side is
// surfaced as an addition (from === null) or a removal (to === null).
const rows = computed<FieldRow[]>(() => {
    const from = props.from ?? {};
    const to = props.to ?? {};
    const keys = Array.from(
        new Set([...Object.keys(from), ...Object.keys(to)])
    ).sort();

    const result: FieldRow[] = [];
    for (const key of keys) {
        const fromPresent = Object.prototype.hasOwnProperty.call(from, key);
        const toPresent = Object.prototype.hasOwnProperty.call(to, key);
        const fromVal = (from as Record<string, unknown>)[key];
        const toVal = (to as Record<string, unknown>)[key];

        if (fromPresent && toPresent && stableEqual(fromVal, toVal)) {
            continue;
        }

        const kind: RowKind = !fromPresent
            ? "added"
            : !toPresent
              ? "removed"
              : "changed";

        result.push({
            key,
            fromDisplay: fromPresent ? formatValue(fromVal) : ABSENT,
            toDisplay: toPresent ? formatValue(toVal) : ABSENT,
            kind,
        });
    }
    return result;
});
</script>

<style scoped>
.field-diff-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.8rem;
}

.field-diff-table th,
.field-diff-table td {
    text-align: left;
    padding: 0.3rem 0.5rem;
    border-bottom: 1px solid var(--p-surface-200);
    vertical-align: top;
}

.field-diff-table th {
    font-weight: 600;
    color: var(--p-text-secondary-color);
    font-size: 0.74rem;
}

.field-diff-key {
    font-family: monospace;
    font-weight: 600;
    white-space: nowrap;
}

.field-diff-value {
    font-family: monospace;
    white-space: pre-wrap;
    word-break: break-word;
}

.field-diff-row.is-changed {
    background: var(--p-yellow-50, #fefce8);
}

.field-diff-row.is-added {
    background: var(--p-green-50, #f0fdf4);
}

.field-diff-row.is-removed {
    background: var(--p-red-50, #fef2f2);
}

.field-diff-row.is-removed .field-diff-to .field-diff-value,
.field-diff-row.is-added .field-diff-from .field-diff-value {
    color: var(--p-text-muted-color);
}

.field-diff-empty {
    margin: 0;
    font-size: 0.78rem;
    color: var(--p-text-muted-color);
}
</style>
